import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type FC,
} from "react";
import { LeftOutlined } from "@ant-design/icons";
import { message } from "antd";
import { useNavigate } from "react-router-dom";

import AppContext from "../../../context/AppContext";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { SIDER_TABS } from "../../../utils/Constants";
import { Button, Form, Input, Select } from "../../shared";
import {
  formatTimezoneDisplay,
  getAvailableTimezones,
} from "../../../utils/dateService";
import { useCreateStaffRegulator } from "../../../queries/staff-regulators/useCreateStaffRegulator";
import { staffRegulatorsErrorMessage } from "../staffRegulatorsErrorMessage";
import {
  STAFF_REGULATORS_LIST_ROUTE,
  buildStaffRegulatorProfilePath,
} from "../staffRegulatorsRoutes";
import {
  STAFF_REGULATOR_FORM_INITIAL_VALUES,
  getDefaultTimezone,
  isWhitelistedIpsInputValid,
  toCreateStaffRegulatorPayload,
  type StaffRegulatorFormValues,
} from "./staffRegulatorFormData";

import styles from "./NewStaffRegulator.module.css";
import { usePageTitle } from "../../CommonComponents/Layout/PageTitleContext";

type AppContextValue = {
  setActiveSidebarTab: (
    tabName: string,
    dropdownOption?: string | false,
  ) => void;
};

const NewStaffRegulator: FC = () => {
  usePageTitle(AppStrings.COMPLIANCE_REGULATORS_NEW);

  const navigate = useNavigate();
  const { setActiveSidebarTab } = useContext(AppContext) as AppContextValue;
  const [form] = Form.useForm<StaffRegulatorFormValues>();
  const createRegulator = useCreateStaffRegulator();

  useEffect(() => {
    setActiveSidebarTab(SIDER_TABS?.COMPLIANCE, "COMPLIANCE_REGULATORS");
  }, [setActiveSidebarTab]);

  const timezoneOptions = useMemo(
    () =>
      getAvailableTimezones().map((tz: string) => ({
        value: tz,
        label: formatTimezoneDisplay(tz),
      })),
    [],
  );

  const handleBack = useCallback(() => {
    navigate(STAFF_REGULATORS_LIST_ROUTE);
  }, [navigate]);

  const handleFinish = useCallback(
    async (values: StaffRegulatorFormValues) => {
      try {
        const created = await createRegulator.mutateAsync(
          toCreateStaffRegulatorPayload(values),
        );
        message.success(AppStrings.COMPLIANCE_REGULATOR_CREATE_SUCCESS);
        navigate(buildStaffRegulatorProfilePath(created.admin_id));
      } catch (error) {
        message.error(
          staffRegulatorsErrorMessage(
            error,
            AppStrings.COMPLIANCE_REGULATOR_CREATE_ERROR,
          ),
        );
      }
    },
    [createRegulator, navigate],
  );

  return (
    <>
      <div className={styles.page} data-testid="new-staff-regulator">
        <div className={styles.header}>
          <button
            type="button"
            className={styles.backButton}
            aria-label={AppStrings.COMPLIANCE_REGULATOR_BACK}
            onClick={handleBack}
          >
            <LeftOutlined className={styles.backButtonIcon} />
          </button>
        </div>

        <Form<StaffRegulatorFormValues>
            form={form}
            initialValues={{
              ...STAFF_REGULATOR_FORM_INITIAL_VALUES,
              timezone: getDefaultTimezone(),
            }}
            onFinish={handleFinish}
            className={styles.form}
            data-testid="new-staff-regulator-form"
          >
            <div className={styles.row}>
              <Form.Item
                name="given_name"
                label={`${AppStrings.COMPLIANCE_REGULATOR_GIVEN_NAME}*`}
                rules={[
                  {
                    required: true,
                    message:
                      AppStrings.COMPLIANCE_REGULATOR_VALIDATION_REQUIRED_GIVEN_NAME,
                  },
                  Form.validators.noWhitespace(
                    AppStrings.COMPLIANCE_REGULATOR_VALIDATION_REQUIRED_GIVEN_NAME,
                  ),
                ]}
              >
                <Input data-testid="staff-regulator-given-name-input" />
              </Form.Item>
              <Form.Item
                name="family_name"
                label={`${AppStrings.COMPLIANCE_REGULATOR_FAMILY_NAME}*`}
                rules={[
                  {
                    required: true,
                    message:
                      AppStrings.COMPLIANCE_REGULATOR_VALIDATION_REQUIRED_FAMILY_NAME,
                  },
                  Form.validators.noWhitespace(
                    AppStrings.COMPLIANCE_REGULATOR_VALIDATION_REQUIRED_FAMILY_NAME,
                  ),
                ]}
              >
                <Input data-testid="staff-regulator-family-name-input" />
              </Form.Item>
            </div>
            <div className={styles.row}>
              <Form.Item
                name="email"
                label={`${AppStrings.EMAIL}*`}
                rules={[
                  {
                    required: true,
                    message: AppStrings.VALIDATION_REQUIRED_EMAIL,
                  },
                  {
                    type: "email",
                    message: AppStrings.VALIDATION_INVALID_EMAIL,
                  },
                ]}
              >
                <Input
                  type="email"
                  data-testid="staff-regulator-email-input"
                />
              </Form.Item>
              <Form.Item
                name="timezone"
                label={`${AppStrings.COMPLIANCE_REGULATOR_TIMEZONE}*`}
                rules={[
                  {
                    required: true,
                    message:
                      AppStrings.COMPLIANCE_REGULATOR_VALIDATION_REQUIRED_TIMEZONE,
                  },
                ]}
              >
                <Select
                  showSearch
                  options={timezoneOptions}
                  data-testid="staff-regulator-timezone-select"
                />
              </Form.Item>
            </div>
            <Form.Item
              name="whitelisted_ips"
              label={AppStrings.COMPLIANCE_REGULATOR_WHITELISTED_IPS}
              extra={AppStrings.COMPLIANCE_REGULATOR_WHITELISTED_IPS_HELP}
              rules={[
                {
                  validator: async (_, value: string) => {
                    if (!isWhitelistedIpsInputValid(value ?? "")) {
                      throw new Error(
                        AppStrings.COMPLIANCE_REGULATOR_WHITELISTED_IPS_INVALID,
                      );
                    }
                  },
                },
              ]}
            >
              <Input
                placeholder={
                  AppStrings.COMPLIANCE_REGULATOR_WHITELISTED_IPS_PLACEHOLDER
                }
                data-testid="staff-regulator-ips-input"
              />
            </Form.Item>
            <div className={styles.footer}>
              <Button
                size="large"
                htmlType="submit"
                loading={createRegulator.isPending}
                data-testid="staff-regulator-submit"
              >
                {AppStrings.COMPLIANCE_REGULATOR_SUBMIT}
              </Button>
            </div>
          </Form>
        </div>
    </>
  );
};

export default NewStaffRegulator;
